/**
 * features/boarding/BoardersPortal.tsx
 * ------------------------------------------------------------------
 * The housemaster's view of their boarders.
 *
 * Deliberately shaped like the PARENT portal and not like a staff screen:
 * a flat list of the pupils this person is responsible for, and one of them
 * open at a time. No class picker, no year group, no school-wide search.
 *
 * WHY THAT MATTERS RATHER THAN BEING A STYLE CHOICE. The obvious build was
 * to reuse GeneratedReports and filter it, but that screen is organised by
 * CLASS and reads `canViewAnyReport` as "every class in the school". The
 * boys' housemaster looks after nineteen pupils spread across Form 1 to A2;
 * handing him the class list to find them would hand him all 826. A
 * housemaster stands in loco parentis for a named set of children, so the
 * screen is the shape of that responsibility — the same shape a parent gets.
 *
 * Scope is enforced in three places, none of which trusts the others:
 *   - here, by listing only boarders in the user's own houses;
 *   - in canViewStudentProfile / boarderIsInMyHouse, for any profile link;
 *   - in getChildAcademics, which re-derives the house server-side and
 *     refuses a pupil who is not in it. That is the one that counts.
 *
 * A housemaster with no houses on their user record sees nobody. That is the
 * safe default: the alternative hands every boarder's report to a new
 * account the moment the role is granted, before anyone has said which
 * house they run.
 *
 * MANAGING THE ROLL. A housemaster adds and removes their own boarders here,
 * but never through a direct write — `admissions.boardingHouse` is the field
 * that decides what they can see, so writing it freely would let them pull
 * any pupil into their house and read that pupil's reports. Both actions go
 * through callables (functions/boarding.js) which enforce the split agreed
 * with the school: the OFFICE decides who boards at all, the HOUSEMASTER
 * decides which house among those. So the "add" list is boarders the office
 * has designated and nobody has housed yet — never the whole roll.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { BedDouble, FileText, Download, Users, AlertCircle, UserPlus, UserMinus, Loader2, X } from 'lucide-react';
import { functions } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { GradientHero, SoftCard } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import StudentAvatar from '@/components/ui/StudentAvatar';
import AcademicPerformance from '@/components/AcademicPerformance';
import { myBoarders } from '@/lib/permissions';
import type { GeneratedReport, MarksDocument, Student } from '@/types';

interface BoarderAcademics {
  reports: GeneratedReport[];
  marksDocs: MarksDocument[];
  classAvgBySubject: Record<string, number>;
}

export default function BoardersPortal() {
  const { students, classes, subjects, teachers, termStatuses } = useData();
  const { userData } = useAuth();

  const boarders = useMemo<Student[]>(
    () =>
      myBoarders(userData ?? null, students).sort((a, b) =>
        (a.fullName || '').localeCompare(b.fullName || ''),
      ),
    [userData, students],
  );

  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [marksDocs, setMarksDocs] = useState<MarksDocument[]>([]);
  const [classAvg, setClassAvg] = useState<Record<string, number>>({});

  // Managing the roll
  const [showAdd, setShowAdd] = useState(false);
  const [candidates, setCandidates] = useState<{ id: string; fullName: string; className: string }[]>([]);
  const [candLoading, setCandLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Student | null>(null);

  useEffect(() => {
    if (!selected && boarders.length > 0) setSelected(boarders[0].id);
  }, [boarders, selected]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getChildAcademics');
        const res = (await fn({ studentId: selected })) as { data: BoarderAcademics };
        if (cancelled) return;
        setReports(
          [...(res.data.reports ?? [])].sort((a, b) =>
            (b.generatedAt ?? '').localeCompare(a.generatedAt ?? ''),
          ),
        );
        setMarksDocs(res.data.marksDocs ?? []);
        setClassAvg(res.data.classAvgBySubject ?? {});
      } catch (e) {
        if (!cancelled) {
          // The server refuses a pupil outside this housemaster's house. If
          // that ever fires it means the list and the rule disagree, which is
          // worth showing rather than rendering an empty screen.
          setError((e as Error).message || 'Could not load this pupil.');
          setReports([]);
          setMarksDocs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  async function openAdd() {
    setShowAdd(true);
    setCandLoading(true);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'listAssignableBoarders');
      const res = (await fn({})) as { data: { candidates: typeof candidates } };
      setCandidates(res.data.candidates ?? []);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
      setCandidates([]);
    } finally {
      setCandLoading(false);
    }
  }

  /** house = one of mine to add, or null to remove. */
  async function assign(studentId: string, house: string | null, name: string) {
    setBusyId(studentId);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'assignBoarder');
      await fn({ studentId, house });
      setMsg({
        ok: true,
        text: house ? `${name} added to your house.` : `${name} removed from your house.`,
      });
      // DataContext is live, so the list re-renders on its own. Only the
      // candidate list is a one-off fetch and needs pruning by hand.
      setCandidates((c) => c.filter((x) => x.id !== studentId));
      if (!house && selected === studentId) setSelected(null);
      setConfirmRemove(null);
    } catch (e) {
      const raw = (e as Error).message || 'Could not update the roll.';
      // The server refuses a pupil the office has not designated. Point at
      // the fix rather than restating the refusal.
      setMsg({
        ok: false,
        text: /not marked as a boarder/i.test(raw)
          ? `${raw} Ask the admissions office to mark them as a boarder, then add them here.`
          : raw,
      });
    } finally {
      setBusyId(null);
    }
  }

  const child = boarders.find((s) => s.id === selected) ?? null;
  const className = (id?: string) => classes.find((c) => c.id === id)?.name ?? '—';
  const houses = userData?.boardingHouses ?? [];

  if (boarders.length === 0) {
    return (
      <div className="space-y-5">
        <GradientHero icon={BedDouble} title="My Boarders" subtitle="Boarding house" />
        <SoftCard>
          <div className="flex items-start gap-3 py-4">
            <AlertCircle size={18} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p className="font-medium text-gray-800 dark:text-gray-100">
                No boarders are assigned to you yet.
              </p>
              <p className="mt-1">
                {houses.length === 0
                  ? 'Your account has no boarding house set. Ask an administrator to add your house to your user record.'
                  : `No pupil is currently marked as a boarder in ${houses.join(', ')}.`}
              </p>
            </div>
          </div>
        </SoftCard>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <GradientHero
        icon={BedDouble}
        title="My Boarders"
        subtitle={`${boarders.length} pupil${boarders.length === 1 ? '' : 's'}${houses.length ? ` · ${houses.join(', ')}` : ''}`}
      />

      {/* The list IS the navigation — the same switcher a parent gets. */}
      <SoftCard>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <Users size={15} /> Boarders in my house
          </h2>
          <button
            type="button"
            onClick={() => (showAdd ? setShowAdd(false) : openAdd())}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20"
          >
            <UserPlus size={13} /> {showAdd ? 'Done' : 'Add a boarder'}
          </button>
        </div>

        {msg && (
          <div className={`mb-3 text-xs rounded-lg px-3 py-2 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
          }`}>
            {msg.text}
          </div>
        )}

        {showAdd && (
          <div className="mb-4 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Pupils the school has marked as boarders who are not yet in a house.
            </p>

            {/* THE MESSAGE THAT MATTERS HERE. A housemaster looking for a boy
                who is not on this list has hit the separation of duties, not a
                bug — the office decides who boards, so an unmarked pupil is
                invisible to them by design. Saying only "nobody is waiting"
                would leave them thinking the system had lost somebody. Day or
                boarder is not yet required at admission, so this will keep
                happening until it is. */}
            <div className="flex items-start gap-2 text-xs rounded-lg px-3 py-2 mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-amber-900 dark:text-amber-200">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                <strong>Someone missing from this list?</strong> They have not been marked as a
                boarder on their record yet. Contact the <strong>admissions office</strong> and ask
                them to mark the pupil as a boarder — they will then appear here for you to add.
              </span>
            </div>

            {candLoading ? (
              <div className="py-4 flex justify-center"><Spinner label="Loading…" /></div>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                Nobody is waiting to be housed. Every pupil marked as a boarder is already in a house.
              </p>
            ) : (
              <div className="space-y-1.5">
                {candidates.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 text-sm">
                    <span className="flex-1 min-w-0 truncate text-gray-800 dark:text-gray-100">
                      {c.fullName}
                      <span className="text-gray-400"> · {c.className || '—'}</span>
                    </span>
                    {/* One button per house. With a single house it reads
                        "Add", as it should; with two it names them, because
                        silently picking the first would put a child in the
                        wrong house and nothing would say so. */}
                    {houses.map((h) => (
                      <button
                        key={h}
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => assign(c.id, h, c.fullName)}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-primary text-white hover:bg-primaryDark disabled:opacity-60 shrink-0"
                      >
                        {busyId === c.id ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
                        {houses.length > 1 ? h : 'Add'}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {boarders.map((s) => {
            const active = s.id === selected;
            return (
              <div
                key={s.id}
                className={`flex items-center gap-2 p-2.5 rounded-xl border transition-colors ${
                  active
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelected(s.id)}
                  className="flex items-center gap-3 text-left flex-1 min-w-0"
                >
                  <StudentAvatar student={s} size="sm" />
                  <div className="min-w-0">
                    <p className={`text-sm truncate ${active ? 'font-semibold text-primary' : 'font-medium text-gray-800 dark:text-gray-100'}`}>
                      {s.fullName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {className(s.currentClass)}
                    </p>
                  </div>
                </button>
                {/* Removal is confirmed, not because it is destructive — the
                    pupil keeps every record — but because it is easy to hit
                    by accident on a phone, and the housemaster then loses
                    sight of a child they are responsible for. */}
                <button
                  type="button"
                  title={`Remove ${s.fullName} from my house`}
                  onClick={() => setConfirmRemove(s)}
                  className="text-gray-300 hover:text-red-600 dark:text-gray-600 dark:hover:text-red-400 shrink-0 p-1"
                >
                  <UserMinus size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </SoftCard>

      {error && (
        <SoftCard>
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </SoftCard>
      )}

      {loading ? (
        <SoftCard>
          <div className="flex justify-center py-8">
            <Spinner label="Loading…" />
          </div>
        </SoftCard>
      ) : child ? (
        <>
          {/* Reports first: it is what a housemaster opens this for. */}
          <SoftCard>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
              <FileText size={15} /> Reports · {child.fullName}
            </h2>
            {reports.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                No reports have been generated for this pupil yet.
              </p>
            ) : (
              <div className="space-y-2">
                {reports.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-white">
                        {r.year} · {(r.term ?? '').replace('Term', 'Term ')}
                      </p>
                      <p className="text-xs text-gray-400">
                        Generated {r.generatedAt ? new Date(r.generatedAt).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    {r.downloadUrl && (
                      <button
                        onClick={() => window.open(r.downloadUrl, '_blank')}
                        className="flex items-center gap-1 text-xs text-primary hover:underline shrink-0"
                      >
                        <Download size={14} /> Open
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SoftCard>

          <AcademicPerformance
            student={child}
            marksDocs={marksDocs}
            subjects={subjects}
            teachers={teachers}
            classAvgBySubject={classAvg}
            termStatuses={termStatuses}
            mode="parent"
          />
        </>
      ) : null}

      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Remove from your house?
              </h3>
              <button onClick={() => setConfirmRemove(null)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
              <span className="font-medium">{confirmRemove.fullName}</span> will no longer
              appear in your list and you will not see their reports. Nothing on their
              record changes, and the office can put them back.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => assign(confirmRemove.id, null, confirmRemove.fullName)}
                disabled={busyId === confirmRemove.id}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
              >
                {busyId === confirmRemove.id ? <Loader2 size={14} className="animate-spin" /> : <UserMinus size={14} />}
                Remove
              </button>
              <button
                onClick={() => setConfirmRemove(null)}
                className="flex-1 text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
